import React from "react";
import { CaseManagementEvent } from "@visitproof/types";
import CaseManagementEventCard from "./CaseManagementEventCard";
import { Button } from "../../../../design/button";
import { Text } from "../../../../design/text";
import moment from "moment";

interface Props {
  events: CaseManagementEvent[];
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onEdit: (event: CaseManagementEvent) => void;
  onDelete: (event: CaseManagementEvent) => void;
}

// Group events by date
const groupEventsByDate = (events: CaseManagementEvent[]) => {
  const groups: { date: string; events: CaseManagementEvent[] }[] = [];
  let currentDate = "";

  events.forEach((event) => {
    // occurredAt can be a Firestore Timestamp (has .toDate), a serialized
    // {_seconds,_nanoseconds} (from httpsCallable), a string, or a Date.
    const raw: any = event.occurredAt;
    const occurredAt = raw?.toDate?.()
      ?? (raw?._seconds != null ? new Date(raw._seconds * 1000) : new Date(raw));
    const dateStr = moment(occurredAt).format("YYYY-MM-DD");

    if (dateStr !== currentDate) {
      currentDate = dateStr;
      groups.push({ date: dateStr, events: [event] });
    } else {
      groups[groups.length - 1].events.push(event);
    }
  });

  return groups;
};

const CaseManagementTimeline: React.FC<Props> = ({
  events,
  hasMore,
  loading,
  onLoadMore,
  onEdit,
  onDelete,
}) => {
  if (events.length === 0 && !loading) {
    return null;
  }

  const groupedEvents = groupEventsByDate(events);

  return (
    <div className="space-y-6">
      {groupedEvents.map((group) => {
        const displayDate = moment(group.date).calendar(null, {
          sameDay: "[Today]",
          lastDay: "[Yesterday]",
          lastWeek: "dddd",
          sameElse: "MMMM D, YYYY",
        });

        return (
          <div key={group.date} className="space-y-3">
            {/* Date header */}
            <div className="sticky top-0 z-10 flex items-center gap-3 py-2 bg-zinc-50/90 dark:bg-zinc-950/90 backdrop-blur-sm">
              <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
              <Text className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                {displayDate}
              </Text>
              <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
            </div>

            {/* Events for this date */}
            <div className="space-y-3">
              {group.events.map((event) => (
                <CaseManagementEventCard
                  key={event.id}
                  event={event}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Load more button */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button outline onClick={onLoadMore} disabled={loading}>
            {loading ? "Loading..." : "Load More"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CaseManagementTimeline;
